import { useRoute, type RouteLocationNormalizedLoadedGeneric } from 'vue-router';
import { useInternational } from '@/common/locale';
import { useRolePermissionStore } from '@/config/stores/rolePermission';
import { useViewModeStore } from '@/common/stores/view-mode';
import { storeToRefs } from 'pinia';
import type {
  RolePermissionKeyType,
  PermissionListAPIDataValue,
  PermissionIdListItem,
} from '@/config/views/permission/rolePermission/rolePermission.type';
import { checkRole } from '@/common/auth/auth.utils';
import { type TabPanels, type TabPanel } from '@/common/utils';
import { PropertyType, usePropertyStore } from '@/common/stores/property';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import type { TargetValidationResult } from '@/common/types/slideDetailAdapter.types';
import { RoleGroupRoleDashboardForm } from '@/api-client/userAndPermission/model';
import { useMyInfoStore } from '@/config/stores/my-info';
import {
  ApiResponseTargetStatusResponse,
  TargetStatusRequestTargetKind,
} from '@/api-client/target/model';
import { usePostV11Target } from '@/api-client/target/queries/target-status-controller/target-status-controller';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { useMessage } from '@/common/inject/useMessage';
import type { AxiosResponse } from 'axios';

export interface UseRolePermission {
  type:
    | 'listOnOff'
    | 'permission'
    | 'menuOnOff'
    | 'detailSlide'
    | 'action'
    | 'move'
    | 'add'
    | 'edit'
    | 'delete';
  showError?: boolean;
  rolePermissionKey?: RolePermissionKeyType;
}

export type TabPermissionMap = TabPanel<string> & { permissionKey?: string };

export const getServerOptionCondition = (propertyData: PropertyType) => {
  if (!propertyData?.podDeletionEnabled) {
    return (permission?: RoleGroupRoleDashboardForm | PermissionIdListItem) => {
      return (
        permission?.translateId !== ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_POD_KILL
      );
    };
  }
  return null;
};

/** Master일 때 메뉴/화면 권한 전부 허용으로 간주하기 위한 stub */
const MASTER_PERMISSION_STUB: PermissionListAPIDataValue = {
  menuOnOff: true,
  listOnOff: true,
  permission: true,
  detailSlide: true,
  action: true,
  add: true,
  edit: true,
  delete: true,
  move: true,
};

export const findPermissionByTranslateId = (
  rolePermission?: RolePermissionKeyType,
  route?: RouteLocationNormalizedLoadedGeneric,
): PermissionListAPIDataValue | undefined => {
  const { myInfo } = storeToRefs(useMyInfoStore());
  if (checkRole.isMaster(myInfo.value.role)) {
    return MASTER_PERMISSION_STUB;
  }

  const { globalPermissionList } = storeToRefs(useRolePermissionStore());
  const { viewMode } = storeToRefs(useViewModeStore());

  return Object.values(globalPermissionList.value || {}).reduce<
    PermissionListAPIDataValue | undefined
  >((acc, category) => {
    if (typeof rolePermission === 'object') {
      if (rolePermission[viewMode.value] === undefined) {
        return (
          acc ||
          category.find((item) => item.translateId === route?.meta.rolePermission?.[viewMode.value])
        );
      }
      return acc || category.find((item) => item.translateId === rolePermission[viewMode.value]);
    }

    return acc || category.find((item) => item.translateId === rolePermission);
  }, undefined);
};

export const checkAllPermissionDenied = (rolePermissionList?: RolePermissionKeyType[]) => {
  if (!rolePermissionList) {
    return false;
  }
  const { myInfo } = storeToRefs(useMyInfoStore());
  if (checkRole.isMaster(myInfo.value.role)) {
    return false;
  }

  return rolePermissionList.every((menuPermissionKey) => {
    return !findPermissionByTranslateId(menuPermissionKey)?.menuOnOff;
  });
};

export const useRolePermission = () => {
  const route = useRoute();

  const { t } = useInternational();
  const { showErrorMsg } = useMessage();

  const { myInfo } = storeToRefs(useMyInfoStore());
  const { propertyData } = storeToRefs(usePropertyStore());
  const checkServerOption = getServerOptionCondition(propertyData.value);

  const isPermissionDenied = ({
    type,
    showError = true,
    rolePermissionKey = route.meta.rolePermission,
  }: UseRolePermission) => {
    const curRolePermission = findPermissionByTranslateId(rolePermissionKey, route);

    if (
      !checkRole.isMaster(myInfo.value.role) &&
      (curRolePermission?.[type] === false || curRolePermission?.[type] === null)
    ) {
      if (showError) {
        showErrorMsg({ msgStr: t('MESSAGE.NO_PERMISSION') });
      }
      return true;
    }

    return false;
  };

  const hasPermissionTo = ({
    type,
    rolePermissionKey = route.meta.rolePermission,
  }: UseRolePermission) => {
    const curRolePermission = findPermissionByTranslateId(rolePermissionKey, route);

    if (
      (!checkRole.isMaster(myInfo.value.role) && curRolePermission?.[type] === false) ||
      checkServerOption?.(curRolePermission) === false
    ) {
      return false;
    }

    return true;
  };

  const hasMenuPermission = (type: UseRolePermission['type'], rolePermissionKey?: string) =>
    rolePermissionKey
      ? hasPermissionTo({
          type,
          rolePermissionKey,
        })
      : true; // 권한키 지정되지 않은 경우 무조건 권한 있는 것으로 판단

  const getTabPanelsByPermission = (
    tabPermissionList: TabPermissionMap[],
    type: UseRolePermission['type'],
  ): TabPanels<string> =>
    tabPermissionList.reduce<TabPanels<string>>((acc, { permissionKey, ...rest }) => {
      if (hasMenuPermission(type, permissionKey)) {
        acc.push(rest);
      }
      return acc;
    }, []);

  return {
    isPermissionDenied,
    hasPermissionTo,
    getTabPanelsByPermission,
  };
};

export const useTargetPermissionCheck = () => {
  const { t } = useInternational();
  const { showErrorMsg } = useMessage();

  const { mutateAsync } = usePostV11Target({
    request: {
      params: {
        frameName: FRAME_NAMES.PERMISSION.TARGET_PERMISSION_CHECK,
      },
    },
  });

  const checkIsValidTarget = async (
    params: {
      targetId: string;
      targetKind: TargetStatusRequestTargetKind;
      fromTime?: string;
      toTime?: string;
    },
    options?: {
      showMessageIfTargetNil?: boolean; // 타겟 문자열이 있는지 확인 여부
      showErrorMessage?: boolean;
      isValidateDeletedTarget?: boolean; // 타겟 삭제 여부 확인 검증 여부 (default: true) - 삭제된 타겟인 경우에도 detail상세에 필요 할 수 있음
    },
  ): Promise<TargetValidationResult> => {
    const {
      showMessageIfTargetNil = true,
      showErrorMessage = true,
      isValidateDeletedTarget = true,
    } = options ?? {};

    if (!params.targetId || !params.targetKind) {
      if (showMessageIfTargetNil && showErrorMessage) {
        showErrorMsg({ msgStr: t('MESSAGE.INVALID_TARGET') });
      }
      return { valid: false, reason: 'invalid_params' };
    }

    try {
      const response: AxiosResponse<ApiResponseTargetStatusResponse> = await mutateAsync({
        data: params,
      });

      const targetStatus = response.data.data?.[0] ?? {};

      if (targetStatus.deleted) {
        if (!isValidateDeletedTarget) return { valid: true, reason: 'deleted' };
        if (showErrorMessage) {
          showErrorMsg({ msgStr: t('MESSAGE.DELETED_TARGET') });
          return { valid: false, reason: 'deleted' };
        }
      }

      return { valid: true };
    } catch {
      if (showErrorMessage) {
        showErrorMsg({ msgStr: t('NOTI.PRODUCT.TEMP_SERVER_ERROR') });
      }
      return { valid: false, reason: 'server_error' };
    }
  };

  return {
    checkIsValidTarget,
  };
};
